import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { workspaceAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useToast } from '../contexts/ToastContext';
import { Loader2, Users, LogIn, UserPlus, AlertCircle, CheckCircle } from 'lucide-react';

const InviteAccept: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { refetch: refetchWorkspace } = useWorkspace();
  const { showToast } = useToast();

  const [invitation, setInvitation] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState('');
  const [alreadyMember, setAlreadyMember] = useState(false);

  useEffect(() => {
    if (!token) return;
    const fetchInvitation = async () => {
      try {
        const res = await workspaceAPI.getInvitation(token);
        setInvitation(res.data);
        if (res.data.accepted) {
          setAlreadyMember(true);
        }
      } catch (err: any) {
        const status = err.response?.status;
        if (status === 404 || status === 410) {
          setError('This invitation is invalid or has expired. Please request a new invite from the workspace owner.');
        } else if (status === 409) {
          setAlreadyMember(true);
          setInvitation(err.response?.data);
        } else {
          setError('Failed to load invitation. Please try again later.');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchInvitation();
  }, [token]);

  const handleAccept = async () => {
    if (!token) return;
    setAccepting(true);
    try {
      const res = await workspaceAPI.acceptInvitation(token);
      const joinedWorkspaceId = res.data.workspace_id;
      // Switch to the newly joined workspace
      if (joinedWorkspaceId) {
        localStorage.setItem('active_workspace_id', joinedWorkspaceId);
        await refetchWorkspace();
      }
      showToast('Successfully joined the workspace!', 'success');
      navigate('/dashboard');
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409) {
        setAlreadyMember(true);
        showToast("You're already a member of this workspace.", 'info');
      } else {
        showToast(detail || 'Failed to accept invitation.', 'error');
      }
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
      <div className="card max-w-md w-full text-center space-y-6">
        {error ? (
          <>
            <div className="w-16 h-16 mx-auto rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Invalid Invitation</h1>
            <p className="text-gray-600 dark:text-gray-400">{error}</p>
            <Link
              to="/"
              className="btn-primary inline-block px-6 py-2.5"
            >
              Go Home
            </Link>
          </>
        ) : alreadyMember ? (
          <>
            <div className="w-16 h-16 mx-auto rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Already a Member</h1>
            <p className="text-gray-600 dark:text-gray-400">
              You're already a member of {invitation?.workspace_name || 'this workspace'}.
            </p>
            <button
              onClick={() => navigate('/dashboard')}
              className="btn-primary w-full py-2.5"
            >
              Go to Dashboard
            </button>
          </>
        ) : (
          <>
            <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center">
              <Users className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Workspace Invitation</h1>
            <div className="space-y-2">
              {invitation?.workspace_name && (
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  {invitation.workspace_name}
                </p>
              )}
              {invitation?.invited_by_name && (
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Invited by {invitation.invited_by_name}
                </p>
              )}
              {invitation?.roles && invitation.roles.length > 0 && (
                <div className="flex flex-wrap justify-center gap-2 mt-3">
                  {invitation.roles.map((role: string) => (
                    <span
                      key={role}
                      className="text-xs font-medium px-2.5 py-1 rounded-lg bg-primary/10 text-primary"
                    >
                      {role}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {isAuthenticated ? (
              <button
                onClick={handleAccept}
                disabled={accepting}
                className="btn-primary w-full py-2.5 flex items-center justify-center space-x-2"
              >
                {accepting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Joining...</span>
                  </>
                ) : (
                  <>
                    <Users className="w-5 h-5" />
                    <span>Join Workspace</span>
                  </>
                )}
              </button>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Log in or sign up to accept this invitation.
                </p>
                <Link
                  to={`/login?redirect=/invite/${token}`}
                  className="btn-primary w-full py-2.5 flex items-center justify-center space-x-2"
                >
                  <LogIn className="w-5 h-5" />
                  <span>Log in to Accept</span>
                </Link>
                <Link
                  to={`/register?redirect=/invite/${token}`}
                  className="block w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all font-medium text-center"
                >
                  <UserPlus className="w-5 h-5 inline mr-2" />
                  Sign Up
                </Link>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default InviteAccept;
